import {onError} from "@apollo/client/link/error";
import {BackendErrorCode} from "../types";
import {reloading} from "@/tools";
import {setBackendError} from "../actions";
import {TStore} from "./types";
import ExpiredAccessTokenHandler from "./expiredAccessTokenHandler";
import {NextLink, Observable, Operation} from "@apollo/client";
import type {Subscription} from "zen-observable-ts";
import {UserManager} from "oidc-client-ts";

export const createErrorHandlerLink = (store: TStore, userManager: UserManager) => {
    const expiredAccessTokenHandler = new ExpiredAccessTokenHandler(store);

    function renewToken(operation: Operation, forward: NextLink) {
        return new Observable((observer) => {
            expiredAccessTokenHandler.handleRequest({operation, forward, observer}, userManager).then();
        });
    }

    return onError(({graphQLErrors, networkError, operation, forward}) => {
        if (graphQLErrors?.length) {
            const err = graphQLErrors[0];
            const code = (err.extensions?.code as BackendErrorCode) ?? "unknown";

            switch (code) {
                case "expired_access_token":
                    console.debug("expired access token, renewing...");
                    return renewToken(operation, forward);
                case "not_authorized":
                    // Wrap promise flow into an Observable so Apollo can continue the chain
                    return new Observable((observer) => {
                        let sub: Subscription | undefined;
                        userManager
                            .getUser()
                            .then((user) => {
                                if (!user) {
                                    console.debug("not authorized, renewing token");
                                    sub = renewToken(operation, forward).subscribe({
                                        next: (v) => observer.next(v),
                                        error: (e) => observer.error(e),
                                        complete: () => observer.complete(),
                                    });
                                } else {
                                    console.debug("not authorized, removing user and reloading");
                                    userManager.removeUser().then(() => {
                                        // Completing the observable before reload avoids hanging subscriptions
                                        observer.complete();
                                        window.location.reload();
                                    });
                                }
                            })
                            .catch((e) => observer.error(e));
                        // Teardown
                        return () => sub?.unsubscribe();
                    });
                case "unknown_access_token":
                    // Side effect then complete the observable (request will be abandoned due to reload)
                    return new Observable((observer) => {
                        userManager.removeUser().then(() => {
                            observer.complete();
                            window.location.reload();
                        });
                        // No ongoing subscription to clean up, but return a noop to satisfy Observable contract
                        return () => {};
                    });
                case "unreachable":
                case "invalid_session":
                case "cant_reach_heimdall":
                case "unknown":
                    store.dispatch(
                        setBackendError({
                            code,
                            details: `Message: ${err.message}` + (err.path ? `\nPath: ${err.path.join(".")}` : ""),
                        }),
                    );
                    return undefined;
            }
        } else if (networkError) {
            // there is not need to show the error dialog when we are reloading the page
            // This was necessary because in Firefox when we reload to initiate logging, the pending transactions
            // receive this error.
            if (!reloading) {
                store.dispatch(setBackendError({code: "unreachable"}));
            }
            return undefined;
        }
        return undefined;
    });
};
